"use client";

import { useCallback, useId, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ds/button";
import { ThemedSelect, themedSelectMenuClass, themedSelectTriggerClass } from "@/components/ui-system/select";
import { cn } from "@/lib/utils/cn";
import type { JsonObject } from "@/lib/api/types/json";

import { RULE_COMPARISON_OPS, type RuleComparisonOp, type RuleConditionsV1 } from "../types/rule-expression";

type ConditionRow = {
  localId: string;
  fieldPath: string;
  op: RuleComparisonOp;
  value: string;
};

function newRow(): ConditionRow {
  return {
    localId:
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`,
    fieldPath: "",
    op: ">",
    value: "",
  };
}

function parseValue(raw: string): number | string | boolean {
  const t = raw.trim();
  if (t === "true") return true;
  if (t === "false") return false;
  if (t !== "" && !Number.isNaN(Number(t)) && String(Number(t)) === t) return Number(t);
  return raw;
}

function rowsToConditions(rows: ConditionRow[]): RuleConditionsV1 {
  const acc: RuleConditionsV1 = {};
  for (const r of rows) {
    const path = r.fieldPath.trim();
    if (!path) continue;
    const parsed = parseValue(r.value);
    const nextBranch: Record<string, number | string | boolean> = { [r.op]: parsed };
    const existing = acc[path];
    if (existing && typeof existing === "object" && existing !== null && !Array.isArray(existing)) {
      acc[path] = { ...(existing as Record<string, number | string | boolean>), ...nextBranch };
    } else {
      acc[path] = nextBranch;
    }
  }
  return acc;
}

export type RuleBuilderFormProps = {
  defaultTenantId: string | null;
  onSubmit: (body: { name: string; tenant_id?: string | null; conditions: JsonObject | null }) => void;
  isSubmitting: boolean;
  errorText: string | null;
};

export function RuleBuilderForm({ defaultTenantId, onSubmit, isSubmitting, errorText }: RuleBuilderFormProps) {
  const formId = useId();
  const [name, setName] = useState("");
  const [bindTenant, setBindTenant] = useState(!!defaultTenantId);
  const [rows, setRows] = useState<ConditionRow[]>(() => [newRow()]);
  const [mode, setMode] = useState<"visual" | "json">("visual");
  const [jsonText, setJsonText] = useState("{}\n");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const nameRef = useRef<HTMLInputElement | null>(null);

  const conditionsFromVisual = useMemo(() => rowsToConditions(rows), [rows]);

  const applyJsonToRows = useCallback((text: string) => {
    const parsed = JSON.parse(text) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      throw new Error("Conditions must be a JSON object");
    }
    const out: ConditionRow[] = [];
    for (const [fieldPath, cond] of Object.entries(parsed)) {
      if (cond && typeof cond === "object" && !Array.isArray(cond)) {
        for (const [op, val] of Object.entries(cond)) {
          if (!RULE_COMPARISON_OPS.includes(op as RuleComparisonOp)) continue;
          out.push({ ...newRow(), fieldPath, op: op as RuleComparisonOp, value: String(val) });
        }
      } else {
        out.push({ ...newRow(), fieldPath, op: "==", value: String(cond) });
      }
    }
    setRows(out.length ? out : [newRow()]);
  }, []);

  const syncJsonFromVisual = useCallback(() => {
    setJsonText(`${JSON.stringify(conditionsFromVisual, null, 2)}\n`);
    setJsonError(null);
  }, [conditionsFromVisual]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const nextFieldErrors: Record<string, string> = {};
    if (!name.trim()) {
      nextFieldErrors.name = "Rule name is required.";
    }
    if (mode === "visual") {
      const firstInvalidRow = rows.find((row) => !row.fieldPath.trim() || !row.value.trim());
      if (firstInvalidRow) {
        if (!firstInvalidRow.fieldPath.trim()) {
          nextFieldErrors[`fieldPath:${firstInvalidRow.localId}`] = "Field path is required.";
        } else if (!firstInvalidRow.value.trim()) {
          nextFieldErrors[`value:${firstInvalidRow.localId}`] = "Value is required.";
        }
      }
    }
    setFieldErrors(nextFieldErrors);
    if (nextFieldErrors.name) {
      nameRef.current?.focus();
      return;
    }
    const firstInvalidKey = Object.keys(nextFieldErrors)[0];
    if (firstInvalidKey) {
      const element = document.querySelector<HTMLElement>(`[data-error-key="${firstInvalidKey}"]`);
      element?.focus();
      return;
    }
    let conditions: JsonObject | null = null;
    if (mode === "json") {
      try {
        const parsed = JSON.parse(jsonText) as unknown;
        if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
          conditions = parsed as JsonObject;
        } else {
          throw new Error("Conditions must be a JSON object");
        }
        setJsonError(null);
      } catch (err) {
        setJsonError(err instanceof Error ? err.message : "Invalid JSON");
        return;
      }
    } else {
      conditions = conditionsFromVisual as JsonObject;
    }

    onSubmit({
      name: name.trim(),
      tenant_id: bindTenant && defaultTenantId ? defaultTenantId : undefined,
      conditions: Object.keys(conditions).length ? conditions : null,
    });
  }

  return (
    <div className="mx-auto max-w-5xl p-ds-4">
      <form
        id={formId}
        onSubmit={handleSubmit}
        className="rounded-card border border-card-border bg-surface-primary p-ds-6 shadow-card"
      >
        <div className="mb-ds-6">
          <h2 className="text-title font-semibold tracking-tight text-foreground">New alert rule</h2>
          <p className="mt-ds-2 max-w-3xl text-caption leading-relaxed text-muted">
            Conditions are stored as JSON and evaluated against device telemetry. The visual builder produces the same
            flat map the engine validates today; raw JSON leaves room for future nested logic.
          </p>
        </div>

        <label className="block text-sm">
          <span className="text-foreground/80">Name</span>
          <input
            ref={nameRef}
            required
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setFieldErrors((prev) => ({ ...prev, name: "" }));
            }}
            className={`ui-control mt-2 w-full ${fieldErrors.name ? "!border-error !ring-1 !ring-error/25" : ""}`}
            placeholder="High temperature - warehouse"
          />
          {fieldErrors.name ? <p className="mt-1 text-caption text-error">{fieldErrors.name}</p> : null}
        </label>

        {defaultTenantId ? (
          <label className="mt-5 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={bindTenant} onChange={(e) => setBindTenant(e.target.checked)} />
            Scope rule to current workspace ({defaultTenantId.slice(0, 8)}...)
          </label>
        ) : null}

        <div className="segment-tabs mt-ds-5">
          <button
            type="button"
            className={cn("segment-tab", mode === "visual" && "segment-tab-active")}
            onClick={() => {
              setMode("visual");
              try {
                applyJsonToRows(jsonText);
              } catch {
                // keep previous rows if JSON is invalid
              }
            }}
          >
            Visual builder
          </button>
          <button
            type="button"
            className={cn("segment-tab", mode === "json" && "segment-tab-active")}
            onClick={() => {
              setMode("json");
              syncJsonFromVisual();
            }}
          >
            JSON
          </button>
        </div>

        {mode === "visual" ? (
          <div className="mt-5 space-y-3">
            {rows.map((row) => (
              <div key={row.localId} className="flex flex-wrap items-end gap-ds-2 rounded-card border border-card-border bg-surface-secondary p-ds-3">
                <label className="min-w-[200px] flex-1 text-xs">
                  <span className="text-foreground/80">Field path</span>
                  <input
                    data-error-key={`fieldPath:${row.localId}`}
                    value={row.fieldPath}
                    onChange={(e) => {
                      setRows((prev) => prev.map((r) => (r.localId === row.localId ? { ...r, fieldPath: e.target.value } : r)));
                      setFieldErrors((prev) => ({ ...prev, [`fieldPath:${row.localId}`]: "" }));
                    }}
                    className={`ui-control mt-2 w-full text-sm ${fieldErrors[`fieldPath:${row.localId}`] ? "!border-error !ring-1 !ring-error/25" : ""}`}
                    placeholder="temperature"
                  />
                  {fieldErrors[`fieldPath:${row.localId}`] ? (
                    <p className="mt-1 text-caption text-error">{fieldErrors[`fieldPath:${row.localId}`]}</p>
                  ) : null}
                </label>
                <label className="text-xs">
                  <span className="text-foreground/80">Op</span>
                  <ThemedSelect
                    value={row.op}
                    onChange={(value) =>
                      setRows((prev) =>
                        prev.map((r) =>
                          r.localId === row.localId ? { ...r, op: value as RuleComparisonOp } : r,
                        ),
                      )
                    }
                    options={RULE_COMPARISON_OPS.map((op) => ({ value: op, label: op }))}
                    ariaLabel="Comparison operator"
                    className="mt-2 min-w-[96px]"
                    triggerClassName={themedSelectTriggerClass}
                    menuClassName={themedSelectMenuClass}
                    optionClassName="rounded-xl text-caption"
                  />
                </label>
                <label className="min-w-[120px] flex-1 text-xs">
                  <span className="text-foreground/80">Value</span>
                  <input
                    data-error-key={`value:${row.localId}`}
                    value={row.value}
                    onChange={(e) => {
                      setRows((prev) => prev.map((r) => (r.localId === row.localId ? { ...r, value: e.target.value } : r)));
                      setFieldErrors((prev) => ({ ...prev, [`value:${row.localId}`]: "" }));
                    }}
                    className={`ui-control mt-2 w-full text-sm ${fieldErrors[`value:${row.localId}`] ? "!border-error !ring-1 !ring-error/25" : ""}`}
                    placeholder="80"
                  />
                  {fieldErrors[`value:${row.localId}`] ? (
                    <p className="mt-1 text-caption text-error">{fieldErrors[`value:${row.localId}`]}</p>
                  ) : null}
                </label>
                <Button type="button" variant="secondary" size="sm" onClick={() => setRows((prev) => prev.filter((r) => r.localId !== row.localId))} disabled={rows.length <= 1}>
                  Remove
                </Button>
              </div>
            ))}
            <button type="button" className="text-xs font-medium text-[var(--btn-primary-to)] hover:underline" onClick={() => setRows((prev) => [...prev, newRow()])}>
              + Add condition
            </button>
          </div>
        ) : (
          <div className="mt-5 space-y-1">
            <label className="text-xs text-foreground/80">Conditions (JSON object)</label>
            <textarea
              value={jsonText}
              onChange={(e) => {
                setJsonText(e.target.value);
                setJsonError(null);
              }}
              rows={16}
              className="ui-control w-full p-3  text-xs"
              spellCheck={false}
            />
            {jsonError ? <p className="text-caption text-error">{jsonError}</p> : null}
          </div>
        )}

        {errorText ? <p className="mt-5 text-caption text-error" role="alert">{errorText}</p> : null}

        <Button type="submit" className="mt-ds-6" disabled={isSubmitting}>
          {isSubmitting ? "Creating..." : "Create rule"}
        </Button>
      </form>
    </div>
  );
}
